import { createContext, useContext, useMemo, type PropsWithChildren } from "react";

import type { NodeSize, PlaceholderVisible, PlaceholderAppearance } from "@corca/data";

export type View = "text" | "expression" | "compact-expression";

export type Text = "default" | "parameter" | "primary" | "unit";

export type NodeRenderContext =
  | "editor"
  | "right-sidebar"
  | "objects-tree"
  | "suggestion"
  | "chat-messages"
  | "chat-input";

type ContextType = {
  renderContext: NodeRenderContext;
  view: View;
  size: NodeSize;
  text: Text;
  placeholderVisible: PlaceholderVisible;
  labelPlaceholderVisible: PlaceholderVisible;
  placeholderAppearance: PlaceholderAppearance;
  debugOutlineNodes: boolean;
  placeholderIndex?: string;
  /**
   * Available width for `WrapperNode` to determine whether to enable rows
   */
  availableWidth?: number;
  /**
   * Render inline content as a computed equation, splitting at the top-level
   * `=` into adjacent formula/result parts. Used by previews (e.g. the
   * reference suggestion preview) that render through `ExpressionNode` rather
   * than `WrapperNode`, which has no `computed` prop to pass down.
   */
  computedSplit?: boolean;
};

const NodeStyleContext = createContext<ContextType>({
  renderContext: "editor",
  view: "expression",
  size: "sm",
  text: "default",
  placeholderVisible: "if-empty",
  labelPlaceholderVisible: "none",
  placeholderAppearance: "dashed",
  debugOutlineNodes: false,
  placeholderIndex: undefined,
  availableWidth: undefined,
  computedSplit: false,
});

export type Props = Partial<ContextType>;

export function useNodeStyle() {
  return useContext(NodeStyleContext);
}

export function NodeStyleProvider({
  renderContext,
  view,
  size,
  text,
  placeholderVisible,
  labelPlaceholderVisible,
  placeholderAppearance,
  placeholderIndex,
  children,
  debugOutlineNodes,
  availableWidth,
  computedSplit,
}: PropsWithChildren<Props>) {
  const parentStyle = useNodeStyle();
  const providerValue: ContextType = useMemo(() => {
    return {
      renderContext: renderContext || parentStyle.renderContext,
      view: view || parentStyle.view,
      size: size || parentStyle.size,
      text: text || parentStyle.text,
      placeholderVisible:
        placeholderVisible != null ? placeholderVisible : parentStyle.placeholderVisible,
      labelPlaceholderVisible:
        labelPlaceholderVisible != null
          ? labelPlaceholderVisible
          : parentStyle.labelPlaceholderVisible,
      placeholderAppearance:
        placeholderAppearance != null ? placeholderAppearance : parentStyle.placeholderAppearance,
      placeholderIndex: placeholderIndex || parentStyle.placeholderIndex,
      debugOutlineNodes: debugOutlineNodes ?? parentStyle.debugOutlineNodes,
      availableWidth: availableWidth || parentStyle.availableWidth,
      computedSplit: computedSplit ?? parentStyle.computedSplit,
    };
  }, [
    renderContext,
    view,
    size,
    text,
    parentStyle,
    placeholderVisible,
    labelPlaceholderVisible,
    placeholderAppearance,
    placeholderIndex,
    debugOutlineNodes,
    availableWidth,
    computedSplit,
  ]);

  return <NodeStyleContext.Provider value={providerValue}>{children}</NodeStyleContext.Provider>;
}
